import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Alert, Typography } from '@mui/material';
import { io } from 'socket.io-client';
import { bindGameResync } from '../../../../utils/bindGameResync';
import { useSnackbar } from 'notistack';
import FlashQuizWaiting from './FlashQuizWaiting';
import QuizQuestion from '../normalMode/QuizQuestion';
import FlashQuizResults from './FlashQuizResults';

const FLASH_QUIZ_STEPS = {
  WAITING: 'waiting',
  QUESTION: 'question',
  RESULTS: 'results'
};

// Wrapper component to properly memoize flashConfig
const QuizQuestionWrapper = ({ session, quizData, onComplete }) => {
  // Extract and memoize flash config from session
  const flashConfig = useMemo(() => {
    const rawRapid = session?.rapid_config;
    // console.log('[QuizQuestionWrapper] Raw rapid_config:', rawRapid);
    const parsedRapid = typeof rawRapid === 'string' ? (() => { 
      try { return JSON.parse(rawRapid); } 
      catch { return {}; } 
    })() : (rawRapid || {});
    
    // console.log('[QuizQuestionWrapper] Parsed rapid_config:', parsedRapid);
    const delaySeconds = typeof parsedRapid.delay_seconds === 'number' ? parsedRapid.delay_seconds : 10;
    const songTimerSeconds = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;
    
    // console.log('[QuizQuestionWrapper] Final flashConfig - song_timer_seconds:', songTimerSeconds, 'delay_seconds:', delaySeconds);
    return { delay_seconds: delaySeconds, song_timer_seconds: songTimerSeconds };
  }, [session?.rapid_config]);

  // Memoize quiz config
  const config = useMemo(() => ({
    difficulty_id: session?.quiz_config?.difficulty_id || 7,
    duration_minutes: 5,
    questions_per_song: session?.quiz_config?.questions_per_song || 3
  }), [session?.quiz_config?.difficulty_id, session?.quiz_config?.questions_per_song]);

  return (
    <QuizQuestion
      config={config}
      quizData={quizData}
      currentQuizletIndex={0}
      onComplete={onComplete}
      mode="flash"
      flashConfig={flashConfig}
    />
  );
};

const FlashQuizMain = ({ session, participant, onBack, onComplete }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Initialize totalScore from participant's current_score if available (for page refresh)
  const initialScore = participant?.current_score || 0;
  
  // State management
  const [currentStep, setCurrentStep] = useState(FLASH_QUIZ_STEPS.WAITING);
  const [currentSong, setCurrentSong] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [totalScore, setTotalScore] = useState(initialScore);
  const [finalPosition, setFinalPosition] = useState(0);
  const [finalLeaderboard, setFinalLeaderboard] = useState([]);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [lastPushedAt, setLastPushedAt] = useState(null);
  const [questionVersion, setQuestionVersion] = useState(0); // forces remount on song switch
  const [quizletCompletionData, setQuizletCompletionData] = useState(null);
  const [stopNotice, setStopNotice] = useState(null);

  // console.log('[FlashQuizMain] Component rendered - Step:', currentStep, 'Session:', !!session, 'Participant:', !!participant);

  // Refs
  const lastSignatureRef = useRef(null);
  const currentStepRef = useRef(currentStep);
  const socketRef = useRef(null);
  const pendingTimeoutRef = useRef(null);
  const isProcessingQuestionRef = useRef(false); // Prevent duplicate question processing
  const hasCompletedQuizletRef = useRef(false); // Prevent duplicate onComplete calls
  const currentQuizletIdRef = useRef(null); // Track current quizlet ID
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);

  // Persist session/participant for resume
  useEffect(() => {
    if (session?.session_id && participant?.participant_id) {
      localStorage.setItem('flash_session_id', String(session.session_id));
      localStorage.setItem('flash_participant_id', String(participant.participant_id));
    }
  }, [session?.session_id, participant?.participant_id]);

  // Handle question received with time-based delivery
  const handleQuestionReceived = useCallback((questionData) => {
    // Prevent duplicate processing
    if (isProcessingQuestionRef.current) {
      // console.log('[FlashQuizMain] Already processing question, skipping duplicate');
      return;
    }
    
    // console.log('[FlashQuizMain] Question received:', questionData);
    const { current_song, questions, delivery_time, pushed_at } = questionData;
    
    if (questions && questions.length > 0) {
      isProcessingQuestionRef.current = true;
      hasCompletedQuizletRef.current = false; // Reset completion flag for new question set
      
      // Clear any pending timeout for previous questions
      if (pendingTimeoutRef.current) {
        // console.log('[FlashQuizMain] Clearing previous pending timeout');
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      
      // Note: signature is already checked in applyNewQuestions before calling this
      if (pushed_at) setLastPushedAt(pushed_at);
      setCurrentSong(current_song);
      
      // Track quizlet ID (same format as in FlashQuizWaiting)
      const quizletId = `${current_song?.songs_master_id || 'song'}_${pushed_at || delivery_time || Date.now()}`;
      currentQuizletIdRef.current = quizletId;
      // console.log('[FlashQuizMain] Set quizlet ID:', quizletId);
      
      // Calculate delay until delivery
      const now = new Date();
      const deliveryDate = new Date(delivery_time);
      const delay = deliveryDate.getTime() - now.getTime();
      
      // console.log(`[FlashQuizMain] Question will be shown in ${Math.max(0, delay / 1000)}s`);
      
      // Extract song_timer_seconds from session rapid_config to use for per-question timer
      const rawRapid = session?.rapid_config;
      const parsedRapid = typeof rawRapid === 'string' ? (() => { 
        try { return JSON.parse(rawRapid); } 
        catch { return {}; } 
      })() : (rawRapid || {});
      const songTimerSeconds = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;
      
      // console.log('[FlashQuizMain] Using song_timer_seconds for base_time:', songTimerSeconds);
      
      // Format data for QuizQuestion component
      const formattedQuizData = {
        quiz_data: [{
          song: current_song,
          questions: questions
        }],
        difficulty: {
          base_time: songTimerSeconds // Use song_timer_seconds from flash quiz config for per-question timer
        }
      };
      
      if (delay > 0) {
        // Show waiting state with countdown (only once)
        enqueueSnackbar(`Question incoming in ${Math.ceil(delay / 1000)}s...`, { variant: 'info' });
        
        // Set timeout to show question at exact delivery time
        pendingTimeoutRef.current = setTimeout(() => {
          // console.log('[FlashQuizMain] Showing question now');
          isProcessingQuestionRef.current = false; // Reset flag when question is shown
          setQuizData(formattedQuizData);
          setQuestionVersion(prev => prev + 1);
          setCurrentStep(FLASH_QUIZ_STEPS.QUESTION);
          // Removed duplicate toast - question is now visible
          pendingTimeoutRef.current = null;
        }, delay);
      } else {
        // Delivery time has passed, show immediately
        // console.log('[FlashQuizMain] Showing question immediately');
        isProcessingQuestionRef.current = false; // Reset flag when question is shown
        setQuizData(formattedQuizData);
        setQuestionVersion(prev => prev + 1);
        setCurrentStep(FLASH_QUIZ_STEPS.QUESTION);
      }
    }
  }, [enqueueSnackbar, session]);

  const consumeStopNotice = useCallback(() => setStopNotice(null), []);

  const handleAdminStop = useCallback((options = {}) => {
    const {
      deliveryTime = null,
      reason = 'admin_stop',
      clearCompletion = true,
    } = options;

    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }

    setQuizData(null);
    setCurrentSong(null);
    setLastPushedAt(null);
    setCurrentStep(FLASH_QUIZ_STEPS.WAITING);
    lastSignatureRef.current = null;
    isProcessingQuestionRef.current = false;
    hasCompletedQuizletRef.current = false;
    currentQuizletIdRef.current = null;

    if (clearCompletion) {
      setQuizletCompletionData(null);
    }

    setStopNotice({
      timestamp: Date.now(),
      reason,
      deliveryTime,
    });
  }, []);

  // Handle answer submission from QuizQuestion
  const submitAnswerToBackend = useCallback(async (answerData) => {
    if (!session?.session_id || !participant?.participant_id) return;
    const baseUrl = process.env.REACT_APP_API_URL;
    
    // Get quizlet_id and song_id from current quizlet
    const quizletId = currentQuizletIdRef.current;
    const songId = answerData.song?.songs_master_id || answerData.song?.song?.songs_master_id || null;
    const questionsCount = answerData.results?.totalQuestions || answerData.results?.questionResults?.length || 0;
    
    const response = await fetch(`${baseUrl}/api/live-quiz/sessions/${session.session_id}/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
      },
      body: JSON.stringify({
        participant_id: participant.participant_id,
        quizlet_results: answerData.results,
        total_score: answerData.results.totalScore,
        quizlet_id: quizletId,
        song_id: songId,
        questions_count: questionsCount
      })
    });
    if (!response.ok) throw new Error('Failed to submit answer');
    return response.json();
  }, [session?.session_id, participant?.participant_id]);

  // Calculate max points for a quizlet based on difficulty config
  const calculateMaxPoints = useCallback(async (difficultyId, questionsPerSong) => {
    if (!difficultyId || !questionsPerSong) return 0;
    
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/lookup/difficulty/${difficultyId}`);
      
      if (response.ok) {
        const difficulty = await response.json();
        const basePoints = difficulty?.base_points || 1000;
        const quickTimeBonusMultiplier = difficulty?.quick_time_bonus_multiplier || 2;
        
        // Max points per question = basePoints * (1 + quickTimeBonusMultiplier)
        const maxPointsPerQuestion = basePoints * (1 + quickTimeBonusMultiplier);
        
        // Max points for quizlet = maxPointsPerQuestion * questionsPerSong
        return Math.round(maxPointsPerQuestion * questionsPerSong);
      }
    } catch (error) {
      console.error('[FlashQuizMain] Error fetching difficulty details:', error);
    }
    
    // Fallback calculation
    const basePoints = 1000;
    const quickTimeBonusMultiplier = 2;
    const maxPointsPerQuestion = basePoints * (1 + quickTimeBonusMultiplier);
    return Math.round(maxPointsPerQuestion * questionsPerSong);
  }, []);

  const handleAnswerSubmitted = useCallback(async (quizletIndex, results) => {
    // Prevent duplicate submissions
    if (hasCompletedQuizletRef.current) {
      // console.log('[FlashQuizMain] Quizlet already completed, ignoring duplicate submission');
      return;
    }
    
    // console.log('[FlashQuizMain] Answer submitted:', { quizletIndex, results });
    hasCompletedQuizletRef.current = true; // Mark as completed immediately
    
    try {
      // Add to user answers
      const answerData = {
        quizletIndex: quizletIndex,
        results: results,
        timestamp: new Date().toISOString(),
        song: currentSong
      };
      setUserAnswers(prev => [...prev, answerData]);
      
      // Update total score
      const newScore = totalScore + results.totalScore;
      setTotalScore(newScore);
      // console.log('[FlashQuizMain] Updated total score:', newScore);
      
      // Submit answer to backend
      await submitAnswerToBackend(answerData);
      
      // Note: quizlet rank will be calculated when quizlet finishes (when admin pushes next song or stops)
      // We'll fetch it later when needed
      
      // Calculate max points based on actual questions in quizlet
      const difficultyId = session?.quiz_config?.difficulty_id || 7;
      const questionsPerSong = session?.quiz_config?.questions_per_song || 3;
      const actualQuestionsCount = results.totalQuestions || questionsPerSong; // Use actual questions count
      const maxPoints = await calculateMaxPoints(difficultyId, actualQuestionsCount);
      
      // Fetch current leaderboard to get user's overall rank
      let userRank = null;
      try {
        const baseUrl = process.env.REACT_APP_API_URL;
        const leaderboardResponse = await fetch(`${baseUrl}/api/live-quiz/sessions/${session.session_id}/leaderboard`);
        if (leaderboardResponse.ok) {
          const leaderboardData = await leaderboardResponse.json();
          const leaderboard = leaderboardData.leaderboard || [];
          const userIndex = leaderboard.findIndex(entry => 
            entry.participant_id === participant?.participant_id
          );
          userRank = userIndex >= 0 ? userIndex + 1 : null;
        }
      } catch (err) {
        console.error('[FlashQuizMain] Error fetching leaderboard for rank:', err);
      }
      
      // Set completion data to show in waiting page
      // totalQuestions should be from config (all questions), not just answered
      const answeredQuestions = results.questionResults?.length || results.totalQuestions || 0;
      setQuizletCompletionData({
        answeredQuestions: answeredQuestions,
        totalQuestions: actualQuestionsCount, // Total questions from config
        timeTaken: Math.round(results.timeTaken || 0),
        pointsScored: results.totalScore || 0,
        maxPoints: maxPoints, // Max points calculated based on actual questions
        rank: userRank,
        quizletRank: null, // Will be fetched when quizlet finishes
        quizletId: currentQuizletIdRef.current,
        timedOut: !!results.timedOut
      });
      
      enqueueSnackbar(`+${results.totalScore} points! Total: ${newScore}`, { variant: 'success' });
      
      // Move back to waiting for next push
      setTimeout(() => {
        // console.log('[FlashQuizMain] Returning to waiting page');
        // Clear any pending timeout
        if (pendingTimeoutRef.current) {
          clearTimeout(pendingTimeoutRef.current);
          pendingTimeoutRef.current = null;
        }
        setCurrentStep(FLASH_QUIZ_STEPS.WAITING);
        setQuizData(null);
        setCurrentSong(null); // Clear current song
        setLastPushedAt(null); // Clear last pushed timestamp
        // Clear signature to allow next question
        lastSignatureRef.current = null;
        isProcessingQuestionRef.current = false; // Reset processing flag
        hasCompletedQuizletRef.current = false; // Reset completion flag for next question set
        currentQuizletIdRef.current = null; // Clear quizlet ID for next question
        // console.log('[FlashQuizMain] Cleared all question data, ready for next question');
      }, 1000);
      
    } catch (error) {
      console.error('[FlashQuizMain] Error submitting answer:', error);
      enqueueSnackbar('Failed to submit answer', { variant: 'error' });
      hasCompletedQuizletRef.current = false; // Reset on error to allow retry
    }
  }, [totalScore, currentSong, enqueueSnackbar, submitAnswerToBackend, session, participant, calculateMaxPoints]);

  // Handle session end
  const handleSessionEnd = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/live-quiz/sessions/${session.session_id}/leaderboard`);
      if (response.ok) {
        const data = await response.json();
        setFinalLeaderboard(data.leaderboard || []);
        const myPosition = data.leaderboard?.find(p => p.participant_id === participant.participant_id);
        if (myPosition) {
          setFinalPosition(myPosition.current_position);
        }
      }
    } catch (error) {
      console.error('[FlashQuizMain] Error fetching final leaderboard:', error);
    }
    setSessionEnded(true);
    setCurrentStep(FLASH_QUIZ_STEPS.RESULTS);
  }, [session?.session_id, participant?.participant_id]);

  // Socket connection for real-time question pushing
  useEffect(() => {
    if (!session?.session_id) return;
    
    // Clean up existing socket if any
    if (socketRef.current) {
      // console.log('[FlashQuizMain] Cleaning up existing socket connection');
      socketRef.current.emit('flash:leave', { sessionId: session.session_id });
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const applyNewQuestions = (data) => {
      // Build a unique signature that includes question IDs for better duplicate detection
      const questionIds = data.questions?.map(q => q.question_id).join(',') || '';
      const signature = `${data.current_song?.songs_master_id || 'na'}|${data.pushed_at || 'na'}|${questionIds}`;
      // console.log('[FlashQuizMain] New question signature:', signature);
      // console.log('[FlashQuizMain] Last signature:', lastSignatureRef.current);
      // console.log('[FlashQuizMain] Current step:', currentStepRef.current);
      // console.log('[FlashQuizMain] Question IDs:', questionIds);
      
      // Check if this exact payload was already processed
      if (lastSignatureRef.current === signature) {
        // console.log('[FlashQuizMain] Duplicate signature detected, skipping');
        return; // already applied
      }
      
      lastSignatureRef.current = signature;
      // console.log('[FlashQuizMain] Applying new questions from socket');
      handleQuestionReceived(data);
    };

    let unbindResync = () => {};
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const socket = io(baseUrl, { transports: ['websocket'] });
      socketRef.current = socket;

      const resyncQuestion = async () => {
        try {
          const res = await fetch(`${baseUrl}/api/live-quiz/flash/sessions/${session.session_id}/current-questions`);
          if (!res.ok) return;
          const data = await res.json();
          if (data?.has_active_questions && Array.isArray(data.questions) && data.questions.length) {
            applyNewQuestions({
              session_id: session.session_id,
              current_song: data.current_song,
              questions: data.questions,
              pushed_at: data.pushed_at,
              delivery_time: data.delivery_time,
              total_questions: data.total_questions,
            });
            return;
          }
          if (data?.has_active_questions === false && currentStepRef.current === FLASH_QUIZ_STEPS.QUESTION) {
            handleAdminStop({ reason: 'resync', clearCompletion: false });
          }
        } catch (_) {}
      };
      
      socket.on('connect', () => {
        socket.emit('flash:join', { sessionId: session.session_id });
        resyncQuestion();
      });
      unbindResync = bindGameResync(() => {
        if (socket.connected) socket.emit('flash:join', { sessionId: session.session_id });
        resyncQuestion();
      });
      
      socket.on('flash:question_pushed', (payload) => {
        if (!payload || payload.session_id !== session.session_id) return;
        // console.log('[FlashQuizMain] Flash question pushed event:', payload);
        
        // Scroll to top when question is pushed
        window.scrollTo({ top: 0, behavior: 'smooth' });

        if (currentStepRef.current === FLASH_QUIZ_STEPS.QUESTION) {
          handleAdminStop({
            deliveryTime: payload.delivery_time,
            reason: 'replaced_by_new_push',
            clearCompletion: true,
          });
        } else {
          // Clear any lingering stop notice so countdown UI shows cleanly when already waiting
          setStopNotice(null);
        }

        // Clear completion data when new question is pushed
        setQuizletCompletionData(null);
        applyNewQuestions(payload);
      });

      socket.on('flash:session_ended', (payload) => {
        if (!payload || payload.session_id !== session.session_id) return;
        // console.log('[FlashQuizMain] Session ended event');
        handleSessionEnd();
      });

      socket.on('flash:question_stopped', (payload) => {
        if (!payload || payload.session_id !== session.session_id) return;
        // console.log('[FlashQuizMain] Question stopped event');
        const shouldClear =
          currentStepRef.current === FLASH_QUIZ_STEPS.QUESTION || !!pendingTimeoutRef.current;
        handleAdminStop({
          reason: payload?.reason || 'admin_stop',
          clearCompletion: shouldClear,
        });
      });
      
    } catch (error) {
      console.error('[FlashQuizMain] Socket connection error:', error);
    }

    return () => {
      try {
        unbindResync();
        // Clear any pending timeouts
        if (pendingTimeoutRef.current) {
          clearTimeout(pendingTimeoutRef.current);
          pendingTimeoutRef.current = null;
        }
        
        if (socketRef.current) {
          // console.log('[FlashQuizMain] Cleaning up socket on unmount');
          socketRef.current.emit('flash:leave', { sessionId: session.session_id });
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      } catch (error) {
        console.error('[FlashQuizMain] Socket disconnect error:', error);
      }
    };
  }, [session?.session_id, handleQuestionReceived, handleSessionEnd, enqueueSnackbar, handleAdminStop]);

  // Defensive: periodic session status check to handle edge cases where socket events are missed
  // Use longer interval to reduce API calls
  useEffect(() => {
    if (!session?.session_id || sessionEnded) return;
    let cancelled = false;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const checkStatus = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/live-quiz/sessions/${session.session_id}`);
        if (!res.ok) return;
        const data = await res.json();
        // If session exists but is not active anymore, move to results
        if (!cancelled && data?.session && data.session.status !== 'active') {
          await handleSessionEnd();
        }
      } catch (_) {
        // ignore network errors; next tick will retry
      }
    };
    // initial check and interval (increased from 8s to 30s to reduce calls)
    checkStatus();
    const iv = setInterval(checkStatus, 30000); // 30 seconds instead of 8
    return () => { cancelled = true; clearInterval(iv); };
  }, [session?.session_id, sessionEnded, handleSessionEnd]);

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case FLASH_QUIZ_STEPS.WAITING:
        return (
          <FlashQuizWaiting
            session={session}
            participant={participant}
            onBack={onBack}
            onQuestionReceived={handleQuestionReceived}
            lastPushedAt={lastPushedAt}
            onSessionEnd={handleSessionEnd}
            quizletCompletionData={quizletCompletionData}
            stopNotice={stopNotice}
            onStopNoticeConsumed={consumeStopNotice}
          />
        );
        
      case FLASH_QUIZ_STEPS.QUESTION:
        if (!quizData) {
          return (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '100vh',
              bgcolor: '#121212'
            }}>
              <Typography variant="h6" sx={{ color: '#fff' }}>
                Loading question...
              </Typography>
            </Box>
          );
        }
        return <QuizQuestionWrapper 
          key={questionVersion}
          session={session}
          quizData={quizData}
          onComplete={handleAnswerSubmitted}
        />;
        
      case FLASH_QUIZ_STEPS.RESULTS:
        return (
          <FlashQuizResults
            totalScore={totalScore}
            position={finalPosition}
            leaderboard={finalLeaderboard}
            userAnswers={userAnswers}
            onBack={onBack}
            onPlayAgain={() => {
              setCurrentStep(FLASH_QUIZ_STEPS.WAITING);
              setTotalScore(0);
              setUserAnswers([]);
              setSessionEnded(false);
            }}
          />
        );
        
      default:
        return (
          <Alert severity="error">
            <Typography>Unknown quiz step</Typography>
          </Alert>
        );
    }
  };

  return (
    <Box>
      {renderCurrentStep()}
    </Box>
  );
};

export default FlashQuizMain;
